import { useRef, type FC } from "react";
import { Box, Grid, Stack } from "@mantine/core";
import { useParams } from "react-router-dom";
import { Rdv2ContinueBanner } from "@jasper/shared";
import { useOrderPage } from "../useOrderPage";
import { OrderPageModals } from "../OrderPageModals";
import { hasOrderDesignToDisplay } from "../OrderDesignContent";
import { OrderBackLink, OrderDetailHeader } from "./OrderDetailHeader";
import { OrderDesignCard } from "./OrderDesignCard";
import { OrderFilesCard } from "./OrderFilesCard";
import { OrderHelpPanel } from "./OrderHelpPanel";
import { OrderInfoRibbon } from "./OrderInfoRibbon";
import { OrderNotesCard } from "./OrderNotesCard";
import { OrderPageError, OrderPageLoading } from "./OrderPageStates";
import { OrderProductsCard } from "./OrderProductsCard";
import { OrderRedoPanel } from "./OrderRedoPanel";
import { OrderRequiredActions } from "./OrderRequiredActions";
import { OrderTraceabilityCard } from "./OrderTraceabilityCard";

export const OrderPageV2: FC = () => {
  const { id } = useParams();
  const state = useOrderPage(id!);
  const { order, expectedDeliveryDate, requests, actions } = state;
  const designCardRef = useRef<HTMLDivElement>(null);

  if (state.isLoading) {
    return <OrderPageLoading />;
  }
  if (state.hasError || !order || !expectedDeliveryDate) {
    return <OrderPageError />;
  }

  return (
    <Stack gap="s5" p="s4">
      <OrderPageModals state={state} />
      <OrderBackLink />
      <OrderRequiredActions
        state={state}
        onReviewDesign={() => designCardRef.current?.scrollIntoView()}
      />
      {state.showRdv2ContinueBanner && (
        <Rdv2ContinueBanner order={order} onSubmit={actions.setOrderToDraft} />
      )}
      <OrderDetailHeader order={order} state={state} />
      {requests.help.opened && <OrderHelpPanel order={order} state={state} />}
      {requests.redo.opened && <OrderRedoPanel order={order} state={state} />}
      <OrderInfoRibbon
        order={order}
        expectedDeliveryDate={expectedDeliveryDate}
        state={state}
      />
      <Grid gap="s5">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="s5">
            <OrderProductsCard order={order} />
            {hasOrderDesignToDisplay(state) && (
              <Box ref={designCardRef}>
                <OrderDesignCard order={order} state={state} />
              </Box>
            )}
            <OrderFilesCard order={order} />
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="s5">
            <OrderNotesCard order={order} state={state} />
            <OrderTraceabilityCard order={order} state={state} />
          </Stack>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
